import { Component, OnInit, Input, Output, EventEmitter, TemplateRef, OnChanges, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AlertService } from '../../../services/common/alert.service';
import { InvolvementService } from '../../../services/matters/involvement.service';

@Component({
  selector: 'app-involvement',
  templateUrl: './involvement.component.html',
  styleUrls: ['./involvement.component.css'],
})
export class InvolvementComponent implements OnInit, OnChanges {
  /**
   * Involvement from parent component
   */
  @Input() involvement: any;

  /**
   * Indicates if involvement has changed on edit
   * @param {EventEmitter<string> = new EventEmitter(} ) involvementChange Variable name
   */
  @Output() involvementChange: EventEmitter<string> = new EventEmitter();

  /**
   * Additional info for the involvement, fetched if additional info is expanded
   * @type {any}
   */
  additional_info: any;

  /**
   * Indicates if involvement is being deleted
   * @type {boolean}
   */
  deletingInvolvement: boolean = false;

  /**
   * Indicates if involvement is being edited
   * @type {boolean}
   */
  editing_involvement: boolean = false;

  /**
   * Indicates if mouse pointer is hovering over the involvement
   * @type {boolean}
   */
  hovering: boolean = false;

  /**
   * Indicates if additional info is being shown
   * @type {boolean}
   */
  more_info_expanded: boolean = false;

  matter_type_name: string;

  /**
   * Constructor
   * @param {AlertService}       private alertService       Bootstrap alert service to show notifications, TODO: to be moved to Noty Service usage
   * @param {InvolvementService} private involvementService Service to access involvements controller in the backend
   * @param {NgbModal}           private modalService       Modal service from ngb
   */
  constructor(private alertService: AlertService, private involvementService: InvolvementService, private modalService: NgbModal) {}

  // Component lifecycle functions

  /**
   * Called on component initialization
   */
  ngOnInit() {
    this.prepareInvolvement();
  }

  /**
   * Called when component variables change
   * @param {SimpleChanges} changes Angular object with variables changed
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.involvement && changes.involvement.currentValue) this.prepareInvolvement();
  }

  // Component methods

  /**
   * Build a Google Maps location-search URL without opening directions mode.
   * @param {string} address Address to display on the map
   */
  googleMapsLocationUrl(address: string): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || '')}`;
  }

  /**
   * Create an outgoing envelope for the involvement
   * @param {number} involvement_id Id of involvement to create an envelope
   * @param {[type]} address_id     Id of address of the involvement contact for the envelope
   */
  createOutgoingEnvelope(involvement_id: number, address_id: number) {
    this.alertService.alert_status_info('Preparing envelope');
    this.involvementService
      .createOutgoingEnvelope(involvement_id, address_id)
      .then((response_json) => {
        if (response_json.success) this.alertService.alert_status_success(response_json.text || 'Outgoing envelope created');
        else this.alertService.alert_status_danger(response_json.text || 'Error in creating outgoing envelope');
      })
      .catch(() => {
        this.alertService.alert_status_danger('Error in creating outgoing envelope');
      });
  }

  /**
   * Delete involvement
   */
  deleteInvolvement() {
    this.deletingInvolvement = true;
    this.involvementService
      .delete(this.involvement.id)
      .then((response_json) => {
        this.deletingInvolvement = false;
        if (response_json.success) {
          this.alertService.alert_status_warning(response_json.text || 'Contact removed from matter');
          this.involvementChange.emit('deleted');
        } else this.alertService.alert_status_danger(response_json.text || 'Error removing contact from matter');
      })
      .catch(() => {
        this.deletingInvolvement = false;
        this.alertService.alert_status_danger('Error removing contact from matter');
      });
  }

  /**
   * Modal confirming involvement delete
   * @param {TemplateRef<any>} deleteModal [description]
   */
  deleteInvolvementModal(deleteModal: TemplateRef<any>) {
    this.modalService.open(deleteModal).result.then(
      (result) => {
        if (result == 'delete') this.deleteInvolvement();
      },
      (reason) => {
        //this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }

  /**
   * Switch to edit mode for the involvement
   */
  editInvolvement() {
    this.editing_involvement = true;
  }

  involvementFormResult(event: any) {
    if (event == 'Form Cancelled') {
      this.editing_involvement = false;
    } else if (event == 'Involvement updated') {
      this.editing_involvement = false;
      this.involvementChange.emit('updated');
    }
  }

  /**
   * Mark if address is present in the given object
   * @param {any} objectWithAddress Object to check for address
   */
  markAddressPresence(objectWithAddress: any) {
    if (objectWithAddress.full_address) {
      if (
        String(objectWithAddress.full_address)
          .replace(/\<br\>*/gi, '')
          .replace(/\,/gi, '')
          .trim().length > 3
      )
        objectWithAddress.addressPresent = true;
      else objectWithAddress.addressPresent = false;
    }
  }

  /**
   * Called to mark mouse has entered the component HTML
   */
  mouseIn() {
    this.hovering = true;
  }

  /**
   * Called to mark mouse has exited the component HTML
   */
  mouseOut() {
    this.hovering = false;
  }

  /**
   * Prepare involvement object on component initialization and when involvement object has changed
   */
  prepareInvolvement() {
    if (this.involvement) {
      if (this.involvement.roles) this.involvement.roles_str = this.involvement.roles.join(', ');
      else this.involvement.roles_str = '';
      this.markAddressPresence(this.involvement);
    }
  }

  /**
   * Load additional info and show for this involvement
   */
  showAdditionalInfo() {
    this.more_info_expanded = !this.more_info_expanded;
    if (!this.additional_info)
      this.involvementService.getAdditionalInfo(this.involvement.id).then((response_json) => {
        this.additional_info = response_json;
        if (this.additional_info) {
          if (this.additional_info.addresses) for (let address of this.additional_info.addresses) if (address) this.markAddressPresence(address);
          if (this.additional_info.relationships) for (let relation of this.additional_info.relationships) if (relation && relation.address) this.markAddressPresence(relation.address);
        }
      });
  }
}
